import { useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { inRange } from '../utils';

const HEADERHEIGHT = 48;

export const useInteractiveCodeScrolling = (
  uuid: string,
  userSelectedBlock: string | null,
  columnLayout: boolean,
  setActiveLines: (id: string | null) => void,
  setUserSelectedBlock: (value: string | null) => void,
): void => {
  const scrollPos = useRef(0);

  const [textBlocks, setTextBlocks] = useState<HTMLDivElement[]>();

  const handleScroll = () => {
    if (textBlocks && textBlocks?.length) {
      const nodes = [...textBlocks];

      let selectedElement = -1;

      // the user is able to select blocks manually so we will keep it selected until the
      // block is scrolled to the top or hidden by scrolling to the bottom
      if (userSelectedBlock) {
        // get the id of the selected node
        const blockId = nodes.findIndex(
          node => node.getAttribute('data-id') === userSelectedBlock,
        );
        if (blockId !== -1) {
          const { top } = nodes[blockId].getBoundingClientRect();
          // deselect the block if it is scrolled to the top or bottom
          // else keep it selected
          if (
            // release if the element is behind the header
            top < HEADERHEIGHT ||
            // release if user is scrolling up
            scrollPos.current > window.scrollY
          ) {
            setUserSelectedBlock(null);
          } else if (blockId.toFixed(10) !== userSelectedBlock) {
            selectedElement = blockId;
          }
        }
      }

      if (selectedElement === -1) {
        // if of the element at the top
        selectedElement = nodes.findIndex((node, index) => {
          const { height } = node.getBoundingClientRect();
          const { offsetTop } = node;

          // note: parent should not contain position:relative
          const delta = offsetTop - window.scrollY - HEADERHEIGHT;

          // we check the first element separately since we know that there is no element above
          if (index === 0 && inRange(delta, 0, height)) {
            return true;
          }
          if (index !== 0) {
            const { height: prevHeight } =
              nodes[index - 1].getBoundingClientRect();

            // check if the previous element is directly above the current element to detect gaps
            if (
              inRange(
                nodes[index - 1].offsetTop + prevHeight,
                node.offsetTop - 1,
                node.offsetTop + 1,
              )
            ) {
              // the current element should be highlighted as long as the scrollpos is in the previous element
              if (
                inRange(
                  window.scrollY + HEADERHEIGHT,
                  nodes[index - 1].offsetTop,
                  node.offsetTop,
                )
              ) {
                return true;
              }
            }
            // if there is a gap apply the same rule as for the first element
            if (inRange(delta, 0, height)) {
              return true;
            }
          }

          return false;
        });
      }

      if (selectedElement !== -1) {
        setActiveLines(nodes[selectedElement].getAttribute('data-id'));
      } else {
        setActiveLines(null);
      }
    }

    scrollPos.current = window.scrollY;
  };

  const debouncedHandleScroll = debounce(handleScroll, 10);

  useEffect(() => {
    if (!columnLayout) {
      window.addEventListener('scroll', debouncedHandleScroll);
    }

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [debouncedHandleScroll, columnLayout]);

  useEffect(() => {
    if (!columnLayout) {
      const elements = document.querySelectorAll<HTMLDivElement>(
        `[data-language='${uuid}']`,
      );
      setTextBlocks(Array.from(elements));
    }
  }, [columnLayout]);
};
